import { createRouter, createWebHashHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { layout } from "@/mega-ui/helpers/function"
import { changeLanguage } from '@/helpers/languageHelpers'
import { isStatisticCookieAccepted } from '@/helpers/cookiesHelpers'
import { getQueryParameterAsInt, initializeRouteData } from '@/helpers/dataHelpers'
import { getAuthenticationStore, getCommonStore, getContactStore, getContractStore, getSettingStore } from '@/helpers/storeHelpers'
import { useGtm } from "@gtm-support/vue-gtm"
import { LanguageEnum } from "@/enums"
import { WritableComputedRef } from 'vue'

import i18n from '@/locales'
import LocalStorageConst from "@/constants/LocalStorageConst"

import {
    account,
    bills,
    consumption,
    home,
    login,
    maintenance,
    notFound,
    offers,
    orders,
    ordersExternal,
    settings,
    subscription,
    support
} from "./route";

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'main',
    component: () => layout('MainLayout'),
    children: [
      notFound,
      home,
      account,
      bills,
      consumption,
      settings,
      offers,
      orders,
      subscription,
      support
    ],
  },
  login,
  maintenance,
  ordersExternal,
]

const router = createRouter({
    history: createWebHashHistory(),
    routes,
    scrollBehavior(to, _from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
        }
      }
      return { x: 0, y: 0 };
    }
})

const gtmAnalytics = (to: RouteLocationNormalized) => {
    if (!navigator.cookieEnabled || !isStatisticCookieAccepted()) {
        return
    }

    const gtm = useGtm();
    if (gtm) {
        gtm.trackEvent({
            event: 'page_view',
            value: to.name,
            'target-properties': to.query,
            pagePath: to.fullPath
        })
    }
}

router.beforeEach((to, _from, next) => {
  const commonStore = getCommonStore()
  const settingStore = getSettingStore()
  const authStore = getAuthenticationStore()

  // Before changing route we clear the modals.
  commonStore?.clearAllModals()

  // Clear individual timers.
  if (settingStore && settingStore.timer) {
    settingStore.clearLoadingInterval()
  }

  // Get language in the query and change it.
  if (to.query.lang) {
    const lang = LanguageEnum[to.query.lang.toString() as LanguageEnum]
    if (lang !== localStorage.getItem(LocalStorageConst.LOCALE)) {
      changeLanguage(authStore, lang, (i18n.global.locale as unknown) as WritableComputedRef<string>, false)
    }
  }

  // If we're not authenticated we go to the login page especially if it is required.
  if (to.meta.withAuth && (!authStore || !authStore.isAuthenticated)) {
    next({ name: 'login-main', query: { redirect: to.fullPath }})
  } else {
    next()
  }
})

router.afterEach((to, _from, _failure) => {
  try {
    if (window) {
      window.scrollTo(0, 0)
    }
    if (!["login-main", "unlock-pin"].includes(to.name?.toString() ?? '')) {
      initializeRouteData(to)
        .then(() => {
          const contractStore = getContractStore()
          const contractId = getQueryParameterAsInt(to.query.contractId)
          if (contractStore && contractId > 0 && contractStore.existContract(contractId)) {
            contractStore.selectContract(contractId)
            contractStore.synchronizeList()
            delete to.query.contractID
          }
        });
    }

    gtmAnalytics(to);

  } catch (e) {
    console.error(e)
  }
})

export default router